import type { UploadFaceImageResultCode } from '@cobliteam/api-lib/src/dashboard/camera/definitions/APITypes';
import { useUploadDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import type { FileMetadata as FileUploadMetadata } from '@cobliteam/ui/components/FileUpload';
import { useRef, useState } from 'react';

type FileMetadata = FileUploadMetadata & {
  faceImageUrl?: string;
};

interface UseImageUploadPoolHookProps {
  maxConcurrency?: number;
}

type UploadDoneHandler = (id: string, result: FileMetadata) => void;

const resultCodeMessage: Record<Exclude<UploadFaceImageResultCode, 'OK'>, FileMetadata['error']> = {
  FACE_NOT_FOUND: {
    message: 'Rosto não identificado',
    icon: 'frown',
  },
  FILE_TOO_LARGE: {
    message: 'Arquivo muito grande',
    icon: 'image-off',
  },
  LOW_QUALITY: {
    message: 'Qualidade insuficiente',
    icon: 'image-off',
  },
  NO_FILE: {
    message: 'Arquivo vazio',
    icon: 'file-x-2',
  },
  OBSTRUCTED_IMAGE: {
    message: 'Foto obstruída',
    icon: 'frown',
  },
  WRONG_CONTENT_TYPE: {
    message: 'Formato inválido',
    icon: 'file-x-2',
  },
};

const useFaceImageUploadPool = ({ maxConcurrency = 1 }: UseImageUploadPoolHookProps = {}) => {
  const { mutateAsync: uploadFaceImage } = useUploadDriverFaceImage();

  const [hasTasks, setHasTasks] = useState(false);
  const queue = useRef<FileMetadata[]>([]);

  let uploadDoneHandler: UploadDoneHandler = () => {};
  const activeCount = useRef(0);

  const upload = async (file: FileMetadata): Promise<FileMetadata> => {
    try {
      const faceImage = await uploadFaceImage({
        blobUrl: file.url,
        fileName: file.name,
      });

      const error = faceImage.code !== 'OK' ? resultCodeMessage[faceImage.code] : undefined;

      return {
        ...file,
        faceImageUrl: faceImage.face_image?.url,
        loading: false,
        error,
      };
    } catch (_e: any) {
      return {
        ...file,
        error: { message: 'Falha ao enviar imagem' },
      };
    }
  };

  const processQueue = () => {
    if (activeCount.current >= maxConcurrency || queue.current.length === 0) {
      setHasTasks(queue.current.length > 0);
      return;
    }

    const file = queue.current.shift();
    if (!file) return;

    activeCount.current += 1;
    setHasTasks(queue.current.length > 0);

    upload(file)
      .then((result) => {
        uploadDoneHandler(result.id, result);
      })
      .finally(() => {
        activeCount.current -= 1;
        processQueue();
      });
  };

  const uploadImages = (files: FileMetadata[]) => {
    queue.current.push(...files);
    setHasTasks(true);
    processQueue();
  };

  const handleDone = (handler: UploadDoneHandler) => {
    uploadDoneHandler = handler;
  };

  return { uploadImages, hasTasks, handleDone };
};

export default useFaceImageUploadPool;
export type { FileMetadata };
